<template>
  <div class="home-new">
    <!-- 标题 -->
    <h1 class="chat-hero-title">{{ $t('home.hero.title') }}</h1>

    <div class="home-chat-card" ref="primaryChatCard">
      <!-- 选项卡 -->
      <div class="chat-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          :class="['chat-tab', { active: activeTab === tab.key }]"
          @click="onTabClick(tab)"
        >{{ tab.label }}</button>
      </div>

      <!-- 输入区 -->
      <div class="chat-body">
        <textarea
          class="chat-textarea"
          v-model="prompt"
          :placeholder="promptPlaceholder"
          rows="4"
          :maxlength="promptMaxlength"
          @keydown.enter.ctrl="handleGenerate"
          @keydown.enter.meta="handleGenerate"
        ></textarea>
        <div class="chat-textarea-footer">
          <span class="chat-count">{{ prompt.length }}/{{ promptMaxlength }}</span>
        </div>
      </div>

      <!-- 底部工具栏：图片/视频 -->
      <div class="chat-toolbar" v-if="activeTab !== 'music'">
        <!-- 模型选择 -->
        <div class="toolbar-model" @click="showModelPicker = !showModelPicker">
          <span class="toolbar-model-name">{{ selectedModelName }}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        <!-- 分隔线 -->
        <div class="toolbar-divider"></div>

        <!-- 简洁参数标签（参考 Pollo AI pill 风格） -->
        <div class="toolbar-params" v-if="selectedModelId && paramPreviewItems.length" @click="showParamModal = true">
          <span class="param-chip" v-for="(item, idx) in paramPreviewItems" v-if="idx < 5" :key="item.key">{{ item.display }}</span>
          <span class="param-chip param-chip-more" v-if="paramPreviewItems.length > 5">+{{ paramPreviewItems.length - 5 }}</span>
          <svg class="param-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        <!-- 生成按钮 -->
        <div v-if="isLoggedIn && selectedModelId && activeTab !== 'music'" class="toolbar-credits">
          <span class="toolbar-credits-label">{{ $t('modelDetail.credits') || 'Credits' }}</span>
          <span class="toolbar-credits-value">{{ requiredCredits }}</span>
        </div>
        <span
          class="toolbar-generate-host"
          :class="{ 'toolbar-generate-host--disabled': !prompt.trim() }"
          :data-tooltip="!prompt.trim() ? $t('home.chatDisabledGenerateHint') : ''"
        >
          <button class="toolbar-generate" @click="handleGenerate" :disabled="!prompt.trim()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            <span>{{ generateButtonText }}</span>
          </button>
        </span>
      </div>

      <!-- 底部工具栏：音乐 -->
      <div class="chat-toolbar" v-if="activeTab === 'music'">
        <div class="toolbar-music-style">
          <div class="music-style-select-wrap">
            <select class="music-style-select" v-model="musicPreset">
              <option v-for="s in musicPresetOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
            </select>
            <svg class="music-style-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
        </div>
        <div v-if="isLoggedIn" class="toolbar-credits">
          <span class="toolbar-credits-label">{{ $t('modelDetail.credits') || 'Credits' }}</span>
          <span class="toolbar-credits-value">{{ requiredMusicCredits }}</span>
        </div>
        <span
          class="toolbar-generate-host"
          :class="{ 'toolbar-generate-host--disabled': !prompt.trim() }"
          :data-tooltip="!prompt.trim() ? $t('home.chatDisabledGenerateHint') : ''"
        >
          <button class="toolbar-generate" @click="handleGenerate" :disabled="!prompt.trim()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            <span>{{ generateButtonText }}</span>
          </button>
        </span>
      </div>

      <!-- 模型下拉（两列联动） -->
      <div class="model-dropdown" v-if="showModelPicker && activeTab !== 'music'" @click.stop>
        <div class="model-dropdown-inner">
          <!-- 左列：品牌分组 -->
          <div class="brand-column">
            <button
              v-for="brand in brandGroups"
              :key="brand.key"
              :class="['brand-item', { active: selectedBrand === brand.key }]"
              @click="selectedBrand = brand.key"
            >
              <span class="brand-name">{{ brand.name }}</span>
              <span class="brand-count">{{ brand.models.length }}</span>
            </button>
          </div>
          <!-- 右列：模型列表 -->
          <div class="models-column">
            <div class="models-search">
              <input type="text" v-model="modelSearch" :placeholder="$t('home.searchModel')" />
            </div>
            <div class="models-list">
              <button
                v-for="m in displayBrandModels"
                :key="m.id"
                :class="['model-item', { active: selectedModelId === m.id }]"
                @click="selectModel(m.id)"
              >
                <div class="model-item-left">
                  <span class="model-item-name">{{ m.name }}</span>
                  <span :class="['model-item-badge', m.badge === 'New' ? 'badge-new' : (m.badge === 'Hot' ? 'badge-hot' : '')]" v-if="m.badge">{{ m.badge }}</span>
                </div>
                <span class="model-item-credits">{{ m.credits }} credits</span>
              </button>
              <div class="model-dropdown-empty" v-if="displayBrandModels.length === 0">No models found</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 快捷工具 -->
    <div class="quick-tools-section">
      <div class="quick-tools-header">
        <h2 class="quick-tools-title">{{ $t('home.quickTools.title') }}</h2>
        <div class="quick-tools-nav">
          <button class="quick-tools-arrow" @click="scrollTools(-1)" :disabled="!canScrollToolsPrev">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button class="quick-tools-arrow" @click="scrollTools(1)" :disabled="!canScrollToolsNext">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 6 15 12 9 18"/></svg>
          </button>
          <router-link class="quick-apps-more" :to="toolsListPath">{{ $t('home.apps.more') }}</router-link>
        </div>
      </div>
      <div class="quick-tools-scroll-wrapper" ref="toolsScrollWrapper">
        <div class="quick-tools-track">
          <router-link
            v-for="tool in quickTools"
            :key="tool.id"
            class="quick-tool-card"
            :to="getToolPath(tool)"
          >
            <div class="quick-tool-cover">
              <template v-if="getDemoImages(tool.id)">
                <ToolDemoCover :images="getDemoImages(tool.id)" :alt="$t('tools.' + tool.id + '.name')" />
              </template>
              <DocFormatCover
                v-else-if="isDocTool(tool.id)"
                :tool-id="tool.id"
                mode="cover"
              />
              <div v-else class="quick-tool-fallback">
                <span class="quick-tool-fallback-icon">{{ tool.icon }}</span>
              </div>
            </div>
            <div class="quick-tool-body">
              <span class="quick-tool-name">{{ $t('tools.' + tool.id + '.name') }}</span>
              <span class="quick-tool-desc">{{ getQuickToolSeoKeyword(tool) }}</span>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <!-- 快捷应用（滚动样式同快捷工具） -->
    <div class="quick-tools-section quick-apps-section">
      <div class="quick-tools-header">
        <h2 class="quick-tools-title">{{ $t('home.apps.quickTitle') }}</h2>
        <div class="quick-tools-nav">
          <button class="quick-tools-arrow" @click="scrollApps(-1)" :disabled="!canScrollAppsPrev">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button class="quick-tools-arrow" @click="scrollApps(1)" :disabled="!canScrollAppsNext">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 6 15 12 9 18"/></svg>
          </button>
          <router-link class="quick-apps-more" :to="appsListPath">{{ $t('home.apps.more') }}</router-link>
        </div>
      </div>
      <div class="quick-tools-scroll-wrapper" ref="appsScrollWrapper">
        <div class="quick-tools-track">
          <router-link
            v-for="app in quickApps"
            :key="'quick-app-' + app.key"
            class="quick-tool-card"
            :to="appPath(app.key)"
            @mouseenter.native="onQuickAppEnter($event)"
            @mouseleave.native="onQuickAppLeave($event)"
          >
            <div class="quick-tool-cover">
              <video
                v-if="app.video"
                class="quick-app-cover-video"
                :data-src="app.video"
                :poster="app.cover || undefined"
                loop
                muted
                playsinline
                preload="none"
              ></video>
              <img v-else-if="app.cover" :src="app.cover" :alt="app.modelName" class="quick-app-cover-img" />
              <div v-else class="quick-tool-fallback">
                <span class="quick-tool-fallback-icon">✦</span>
              </div>
            </div>
            <div class="quick-tool-body">
              <span class="quick-tool-name">{{ app.modelName }}</span>
              <span class="quick-tool-desc">{{ app.keyword }}</span>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <!-- 探索更多 AI 功能：/tools 全量，换行铺满 -->
    <div class="explore-more-section">
      <div class="explore-more-header">
        <h2 class="quick-tools-title">{{ $t('home.exploreMore.title') }}</h2>
      </div>
      <div
        v-if="showExploreChatCard"
        ref="exploreChatCard"
        :key="'explore-chat-' + exploreChatAnimationKey"
        :class="[
          'explore-chat-card-slot',
          exploreChatAnimationActive ? 'explore-chat-card-slot--expanded' : '',
          exploreChatExpanded ? 'explore-chat-card-slot--full' : '',
          exploreChatExpansionFinished ? 'explore-chat-card-slot--open' : ''
        ]"
        @transitionend="onExploreChatSlotTransitionEnd"
      >
      <div v-if="!exploreChatExpanded" class="explore-chat-compact">
        <div class="explore-chat-compact-main">
          <div class="explore-chat-compact-input">
          <button
            type="button"
            class="explore-chat-compact-prompt"
            @click="expandExploreChatCard"
          >
            <span class="explore-chat-compact-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 3l1.4 4.1L17.5 8.5l-4.1 1.4L12 14l-1.4-4.1-4.1-1.4 4.1-1.4L12 3z"/>
                <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z"/>
              </svg>
            </span>
            <span class="explore-chat-compact-content">
              <span :class="['explore-chat-compact-text', { 'has-value': prompt.trim() }]">
                {{ prompt.trim() || $t('home.compactChatPlaceholder') }}
              </span>
            </span>
          </button>
          <span
            class="toolbar-generate-host"
            :class="{ 'toolbar-generate-host--disabled': !prompt.trim() }"
            :data-tooltip="!prompt.trim() ? $t('home.chatDisabledGenerateHint') : ''"
          >
            <button class="toolbar-generate" @click.stop="handleGenerate" :disabled="!prompt.trim()">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              <span>{{ generateButtonText }}</span>
            </button>
          </span>
          </div>
        </div>
      </div>

      <div v-else class="home-chat-card home-chat-card--explore">
        <!-- 顶部输入框离开视口后，在探索区展示同一套生成控件 -->
        <div class="chat-tabs">
          <button
            v-for="tab in tabs"
            :key="'explore-' + tab.key"
            :class="['chat-tab', { active: activeTab === tab.key }]"
            @click="onTabClick(tab)"
          >{{ tab.label }}</button>
        </div>

        <div class="chat-body">
          <textarea
            ref="explorePromptTextarea"
            class="chat-textarea"
            v-model="prompt"
            :placeholder="promptPlaceholder"
            rows="4"
            :maxlength="promptMaxlength"
            @keydown.enter.ctrl="handleGenerate"
            @keydown.enter.meta="handleGenerate"
          ></textarea>
          <div class="chat-textarea-footer">
            <span class="chat-count">{{ prompt.length }}/{{ promptMaxlength }}</span>
          </div>
        </div>

        <div class="chat-toolbar" v-if="activeTab !== 'music'">
          <div class="toolbar-model" @click="showModelPicker = !showModelPicker">
            <span class="toolbar-model-name">{{ selectedModelName }}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div class="toolbar-divider"></div>
          <div class="toolbar-params" v-if="selectedModelId && paramPreviewItems.length" @click="showParamModal = true">
            <span class="param-chip" v-for="(item, idx) in paramPreviewItems" v-if="idx < 5" :key="'explore-' + item.key">{{ item.display }}</span>
            <span class="param-chip param-chip-more" v-if="paramPreviewItems.length > 5">+{{ paramPreviewItems.length - 5 }}</span>
            <svg class="param-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          <div v-if="isLoggedIn && selectedModelId" class="toolbar-credits">
            <span class="toolbar-credits-label">{{ $t('modelDetail.credits') || 'Credits' }}</span>
            <span class="toolbar-credits-value">{{ requiredCredits }}</span>
          </div>
          <span
            class="toolbar-generate-host"
            :class="{ 'toolbar-generate-host--disabled': !prompt.trim() }"
            :data-tooltip="!prompt.trim() ? $t('home.chatDisabledGenerateHint') : ''"
          >
            <button class="toolbar-generate" @click="handleGenerate" :disabled="!prompt.trim()">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              <span>{{ generateButtonText }}</span>
            </button>
          </span>
        </div>

        <div class="chat-toolbar" v-if="activeTab === 'music'">
          <div class="toolbar-music-style">
            <div class="music-style-select-wrap">
              <select class="music-style-select" v-model="musicPreset">
                <option v-for="s in musicPresetOptions" :key="'explore-' + s.value" :value="s.value">{{ s.label }}</option>
              </select>
              <svg class="music-style-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>
          </div>
          <div v-if="isLoggedIn" class="toolbar-credits">
            <span class="toolbar-credits-label">{{ $t('modelDetail.credits') || 'Credits' }}</span>
            <span class="toolbar-credits-value">{{ requiredMusicCredits }}</span>
          </div>
          <span
            class="toolbar-generate-host"
            :class="{ 'toolbar-generate-host--disabled': !prompt.trim() }"
            :data-tooltip="!prompt.trim() ? $t('home.chatDisabledGenerateHint') : ''"
          >
            <button class="toolbar-generate" @click="handleGenerate" :disabled="!prompt.trim()">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              <span>{{ generateButtonText }}</span>
            </button>
          </span>
        </div>

        <div class="model-dropdown" v-if="showModelPicker && activeTab !== 'music'" @click.stop>
          <div class="model-dropdown-inner">
            <div class="brand-column">
              <button
                v-for="brand in brandGroups"
                :key="'explore-' + brand.key"
                :class="['brand-item', { active: selectedBrand === brand.key }]"
                @click="selectedBrand = brand.key"
              >
                <span class="brand-name">{{ brand.name }}</span>
                <span class="brand-count">{{ brand.models.length }}</span>
              </button>
            </div>
            <div class="models-column">
              <div class="models-search">
                <input type="text" v-model="modelSearch" :placeholder="$t('home.searchModel')" />
              </div>
              <div class="models-list">
                <button
                  v-for="m in displayBrandModels"
                  :key="'explore-' + m.id"
                  :class="['model-item', { active: selectedModelId === m.id }]"
                  @click="selectModel(m.id)"
                >
                  <div class="model-item-left">
                    <span class="model-item-name">{{ m.name }}</span>
                    <span :class="['model-item-badge', m.badge === 'New' ? 'badge-new' : (m.badge === 'Hot' ? 'badge-hot' : '')]" v-if="m.badge">{{ m.badge }}</span>
                  </div>
                  <span class="model-item-credits">{{ m.credits }} credits</span>
                </button>
                <div class="model-dropdown-empty" v-if="displayBrandModels.length === 0">No models found</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      <ul class="explore-more-grid">
        <li
          v-for="tool in tools"
          :key="'explore-' + tool.id"
          :class="['explore-more-item', exploreMediaMap[tool.id] && exploreMediaMap[tool.id].kind === 'doc' ? 'explore-more-item--doc' : '']"
        >
          <img
            v-if="exploreMediaMap[tool.id] && exploreMediaMap[tool.id].kind === 'image'"
            class="explore-more-cover"
            :src="exploreMediaMap[tool.id].src"
            :alt="getToolText(tool.id, 'name')"
            loading="lazy"
            decoding="async"
          />
          <video
            v-else-if="exploreMediaMap[tool.id] && exploreMediaMap[tool.id].kind === 'video'"
            class="explore-more-cover explore-more-cover-video"
            :src="exploreMediaMap[tool.id].src"
            muted
            loop
            autoplay
            playsinline
            preload="metadata"
          ></video>
          <div
            v-else-if="exploreMediaMap[tool.id] && exploreMediaMap[tool.id].kind === 'doc'"
            class="explore-more-doc"
            aria-hidden="true"
          >
            <div
              class="explore-more-doc-side"
              :style="{ background: exploreMediaMap[tool.id].sourceBg }"
            ></div>
            <div
              class="explore-more-doc-side"
              :style="{ background: exploreMediaMap[tool.id].targetBg }"
            ></div>
          </div>
          <div v-else class="explore-more-fallback" aria-hidden="true">
            <span>{{ tool.icon }}</span>
          </div>
          <router-link class="explore-more-link" :to="getToolPath(tool)" :title="getExploreToolLabel(tool.id)">
            <span class="explore-more-label">{{ getExploreToolLabel(tool.id) }}</span>
          </router-link>
        </li>
      </ul>
    </div>

    <!-- 参数弹窗 -->
    <div class="modal-overlay" v-if="showParamModal" @click.self="showParamModal = false">
      <div class="param-modal">
        <div class="param-modal-header">
          <h3>{{ selectedModelName }}</h3>
          <button class="pm-close" @click="showParamModal = false">&times;</button>
        </div>
        <div class="param-modal-body">
          <ModelParams
            v-if="selectedModelId"
            :modelId="selectedModelId"
            :compact="false"
            :values="paramValues"
            :uploadLoading="uploadLoadingMap"
            :uploadFileNames="uploadFileNames"
            @change="onParamChange"
            @upload="onParamUpload"
            @remove-upload="onRemoveUpload"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ModelParams from '../components/common/ModelParams.vue'
import { getModelParams, getDefaultValues } from '../config/modelParams'
import { calcModelCredits } from '../config/modelCredits'

var getToolDemoImages = require('@/config/toolDemoImages').getToolDemoImages
var isDocumentTool = require('@/config/docConvertVisuals').isDocumentTool
var getDocConvertVisual = require('@/config/docConvertVisuals').getDocConvertVisual
var DocFormatCover = require('@/components/DocFormatCover.vue').default
var ToolDemoCover = require('@/components/ToolDemoCover.vue').default
var appConfig = require('../config/appConfig')
var quickToolSeoKeywords = require('../config/quickToolSeoKeywords')

function hexToRgba(hex, alpha) {
  var h = String(hex || '').replace('#', '')
  if (h.length === 3) {
    h = h.charAt(0) + h.charAt(0) + h.charAt(1) + h.charAt(1) + h.charAt(2) + h.charAt(2)
  }
  var n = parseInt(h, 16)
  if (isNaN(n)) return 'rgba(229, 231, 235, ' + alpha + ')'
  return 'rgba(' + ((n >> 16) & 255) + ', ' + ((n >> 8) & 255) + ', ' + (n & 255) + ', ' + alpha + ')'
}

function buildExploreMedia(tool) {
  var empty = { kind: '' }
  if (!tool) return empty
  if (isDocumentTool(tool.id)) {
    var visual = getDocConvertVisual(tool.id)
    if (!visual || !visual.source || !visual.target) return empty
    return {
      kind: 'doc',
      sourceBg: hexToRgba(visual.source.color, 0.32),
      targetBg: hexToRgba(visual.target.color, 0.32)
    }
  }
  var images = getToolDemoImages(tool.id)
  if (!images || !images.length) return empty
  var i
  var src
  for (i = images.length - 1; i >= 0; i--) {
    src = images[i]
    if (!/\.mp4(\?|#|$)/i.test(String(src))) {
      return { kind: 'image', src: src }
    }
  }
  return { kind: 'video', src: images[images.length - 1] }
}

export default {
  name: 'Home',
  components: { ModelParams: ModelParams, DocFormatCover: DocFormatCover, ToolDemoCover: ToolDemoCover },
  data: function () {
    return {
      activeTab: 'image',
      prompt: '',
      showModelPicker: false,
      showParamModal: false,
      modelSearch: '',
      selectedModelId: '',
      selectedBrand: '',
      paramValues: {},
      uploadLoadingMap: {},
      uploadFileNames: {},
      musicPreset: 'Upbeat Pop Anthem',
      toolScrollPos: 0,
      toolMaxScroll: 0,
      appScrollPos: 0,
      appMaxScroll: 0,
      showExploreChatCard: false,
      exploreChatAnimationKey: 0,
      exploreChatAnimationActive: false,
      exploreChatExpansionFinished: false,
      exploreChatExpanded: false,
      _primaryChatObserver: null,
      _exploreChatObserver: null,
      _primaryChatFallbackHandler: null,
      _quickAppPlayingVideo: null
    }
  },
  computed: {
    tabs: function () {
      return [
        { key: 'image', label: this.$t('footer.aiImages') },
        { key: 'video', label: this.$t('footer.aiVideos') },
        { key: 'music', label: this.$t('footer.aiMusic') }
      ]
    },
    currentModels: function () {
      if (this.activeTab === 'image') return this.$store.state.imageModels || []
      if (this.activeTab === 'video') return this.$store.state.videoModels || []
      return []
    },
    brandGroups: function () {
      var tab = this.activeTab
      var models = this.currentModels
      var modelMap = {}
      for (var i = 0; i < models.length; i++) {
        modelMap[models[i].id] = models[i]
      }
      var brandDefs = tab === 'image' ? [
        { key: 'seedream', name: 'Seedream', ids: ['seedream-5', 'seedream-4'] },
        { key: 'flux', name: 'FLUX', ids: ['flux-2', 'flux-pro-1-1-ultra', 'flux-schnell'] },
        { key: 'midjourney', name: 'Midjourney', ids: ['midjourney'] },
        { key: 'gpt-image', name: 'GPT Image', ids: ['gpt-image-1', 'gpt-image-1-5'] },
        // { key: 'nano-banana', name: 'Nano Banana', ids: ['nano-banana-2'] }, // 暂时隐藏
        { key: 'qwen', name: 'Qwen', ids: ['qwen-image-2-0-pro'] },
        { key: 'z-image', name: 'Z Image', ids: ['z-image-turbo'] },
        { key: 'stable-diffusion', name: 'Stable Diffusion', ids: ['sd-3-5'] }
      ] : [
        { key: 'wan', name: 'Wan', ids: ['wan-2-6', 'wan-2-5', 'wan-2-2', 'wan-animate'] },
        { key: 'kling', name: 'Kling', ids: ['kling-3', 'kling-2-6', 'kling-2-5'] },
        { key: 'veo', name: 'Veo', ids: ['veo-3-1', 'veo-3-0'] },
        { key: 'seedance', name: 'Seedance', ids: ['seedance'] },
        { key: 'pollodance', name: 'Pollo Dance', ids: ['pollodance'] },
        { key: 'seegen', name: 'SeeGen', ids: ['seegen'] },
        { key: 'sora', name: 'Sora', ids: ['sora-2'] },
        { key: 'vidu', name: 'Vidu', ids: ['vidu-q3-pro'] },
        { key: 'hailuo', name: 'Hailuo', ids: ['hailuo-2-3'] },
        { key: 'runway', name: 'Runway', ids: ['runway'] }
      ]
      var groups = []
      for (var g = 0; g < brandDefs.length; g++) {
        var def = brandDefs[g]
        var groupModels = []
        for (var m = 0; m < def.ids.length; m++) {
          if (modelMap[def.ids[m]]) {
            groupModels.push(modelMap[def.ids[m]])
          }
        }
        if (groupModels.length > 0) {
          groups.push({ key: def.key, name: def.name, models: groupModels })
        }
      }
      return groups
    },
    currentBrandModels: function () {
      if (!this.selectedBrand) return []
      var groups = this.brandGroups
      for (var i = 0; i < groups.length; i++) {
        if (groups[i].key === this.selectedBrand) return groups[i].models
      }
      return []
    },
    displayBrandModels: function () {
      var s = this.modelSearch.toLowerCase()
      if (s) {
        return this.currentModels.filter(function (m) {
          return m.name.toLowerCase().indexOf(s) > -1 || m.id.toLowerCase().indexOf(s) > -1
        })
      }
      return this.currentBrandModels
    },
    selectedModelName: function () {
      if (!this.selectedModelId) return this.$t('home.selectModel') || 'Select Model'
      var models = this.currentModels
      for (var i = 0; i < models.length; i++) {
        if (models[i].id === this.selectedModelId) return models[i].name
      }
      return this.selectedModelId
    },
    promptPlaceholder: function () {
      return this.$t('home.compactChatPlaceholder') || 'Enter your prompt to generate stunning AI images, videos and original music'
    },
    promptMaxlength: function () {
      if (this.activeTab === 'music') return 3000
      var config = getModelParams(this.selectedModelId)
      return (config && config.promptMaxlength) || 2000
    },
    tools: function () {
      return this.$store.state.tools || []
    },
    quickTools: function () {
      var list = this.tools.slice()
      var priorityIds = [
        'watermark-remover',
        'handwritten-erase',
        'doc-crop-enhance',
        'object-remover',
        'face-swap'
      ]
      var bottomPriorityIds = [
        'background-remover',
        'background-blur',
        'photo-restore',
        'ai-change-clothes',
        'blue-background'
      ]
      var topRow = []
      var bottomRow = []
      var remaining = []
      var i
      var j
      for (i = 0; i < priorityIds.length; i++) {
        for (j = 0; j < list.length; j++) {
          if (list[j].id === priorityIds[i]) {
            topRow.push(list[j])
            break
          }
        }
      }
      for (i = 0; i < bottomPriorityIds.length; i++) {
        for (j = 0; j < list.length; j++) {
          if (list[j].id === bottomPriorityIds[i]) {
            bottomRow.push(list[j])
            break
          }
        }
      }
      for (i = 0; i < list.length; i++) {
        if (priorityIds.indexOf(list[i].id) < 0 && bottomPriorityIds.indexOf(list[i].id) < 0) {
          remaining.push(list[i])
        }
      }

      var topRowCount = Math.ceil(list.length / 2)
      while (topRow.length < topRowCount && remaining.length) {
        topRow.push(remaining.shift())
      }
      while (bottomRow.length < list.length - topRowCount && remaining.length) {
        bottomRow.push(remaining.shift())
      }
      var ordered = []
      var rowLength = Math.max(topRow.length, bottomRow.length)
      for (i = 0; i < rowLength; i++) {
        if (topRow[i]) ordered.push(topRow[i])
        if (bottomRow[i]) ordered.push(bottomRow[i])
      }
      return ordered
    },
    exploreMediaMap: function () {
      var map = {}
      var list = this.tools
      var i
      for (i = 0; i < list.length; i++) {
        map[list[i].id] = buildExploreMedia(list[i])
      }
      return map
    },
    quickApps: function () {
      return appConfig.getQuickApps(this.$t.bind(this), this.$i18n.locale)
    },
    appsListPath: function () {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + '/apps' : '/apps'
    },
    toolsListPath: function () {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + '/tools' : '/tools'
    },
    canScrollToolsPrev: function () {
      return this.toolScrollPos > 1
    },
    canScrollToolsNext: function () {
      return this.toolScrollPos < this.toolMaxScroll - 1
    },
    canScrollAppsPrev: function () {
      return this.appScrollPos > 1
    },
    canScrollAppsNext: function () {
      return this.appScrollPos < this.appMaxScroll - 1
    },
    musicPresetOptions: function () {
      var raw = this.$t('home.musicPresets')
      if (Array.isArray(raw)) return raw
      return []
    },
    paramPreviewItems: function () {
      if (!this.selectedModelId) return []
      var config = getModelParams(this.selectedModelId)
      if (!config) return []
      var items = []
      var params = config.params
      /* 额外展示的参数 key（不受 showInCompact 限制） */
      var extraKeys = {
        aspectRatio: true,
        videoDuration: true,
        videoLength: true,
        generateAudio: true,
        imageCount: true,
        selectedVariant: true,
        outputStyle: true,
        resolution: true,
        imageSize: true,
        genType: true,
        modelVersion: true,
        animateMode: true,
        veoGenType: true
      }
      /* 不适合在预览中显示的类型 */
      var skipTypes = { hidden: true, upload: true, 'text-input': true, 'number-input': true }
      /* 检测是否有自定义宽高参数 */
      var hasCustomSize = false
      for (var c = 0; c < params.length; c++) {
        if (params[c].key === 'customWidth' || params[c].key === 'customHeight') {
          hasCustomSize = true
          break
        }
      }
      for (var i = 0; i < params.length; i++) {
        var p = params[i]
        if (!p.showInCompact && !extraKeys[p.key]) continue
        if (skipTypes[p.type]) continue
        if (p.condition && !p.condition(this.paramValues)) continue
        var value = this.paramValues[p.key]
        var display = this.formatParamPreview(p, value)
        if (display) {
          items.push({ key: p.key, display: display })
        }
      }
      /* 自定义宽高合并为一个尺寸芯片（仅在自定义尺寸模式下，无 sizeMode 的模型始终显示） */
      if (hasCustomSize) {
        var sizeMode = this.paramValues.sizeMode
        var showSize = !sizeMode || sizeMode === 'custom'
        if (showSize) {
          var w = this.paramValues.customWidth
          var h = this.paramValues.customHeight
          if (w && h) {
            items.unshift({ key: 'customSize', display: w + '×' + h })
          }
        }
      }
      return items
    },
    userCredits: function () {
      var storeUser = this.$store && this.$store.state ? this.$store.state.userInfo : null
      if (storeUser && storeUser.credits != null) return Number(storeUser.credits) || 0
      try {
        var raw = localStorage.getItem('userInfo')
        if (!raw) return 0
        var parsed = JSON.parse(raw)
        return parsed && parsed.credits != null ? (Number(parsed.credits) || 0) : 0
      } catch (e) {
        return 0
      }
    },
    isLoggedIn: function () {
      return !!(this.$store && this.$store.state && this.$store.state.userInfo)
    },
    requiredCredits: function () {
      if (!this.selectedModelId || this.activeTab === 'music') return 0
      var selected = null
      var models = this.currentModels || []
      for (var i = 0; i < models.length; i++) {
        if (models[i].id === this.selectedModelId) {
          selected = models[i]
          break
        }
      }
      var baseCredits = selected && selected.credits != null ? Number(selected.credits) : 0
      return calcModelCredits({
        modelId: this.selectedModelId,
        baseCredits: baseCredits || 0,
        durationSec: this.paramValues.videoDuration || this.paramValues.videoLength,
        resolutionKey: this.paramValues.videoRes || this.paramValues.imageSize || this.paramValues.resolution,
        audioOn: this.paramValues.generateAudio,
        genType: this.paramValues.veoGenType || this.paramValues.genType,
        count: this.paramValues.imageCount || 1,
        imageSize: this.paramValues.imageSize,
        modelVersion: this.paramValues.modelVersion
      })
    },
    insufficientCredits: function () {
      if (!this.isLoggedIn) return false
      if (this.activeTab === 'music') return this.userCredits < this.requiredMusicCredits
      if (!this.selectedModelId) return false
      return this.userCredits < this.requiredCredits
    },
    generateButtonText: function () {
      if (this.insufficientCredits) return this.$t('modelDetail.getMoreCredits')
      return this.$t('home.generate')
    },
    requiredMusicCredits: function () {
      return 20
    }
  },
  watch: {
    activeTab: function () {
      this.selectedModelId = ''
      this.selectedBrand = ''
      var self = this
      self.$nextTick(function () {
        if (self.brandGroups.length > 0) self.selectedBrand = self.brandGroups[0].key
        if (self.currentModels.length > 0) self.selectModel(self.currentModels[0].id)
      })
    }
  },
  mounted: function () {
    var self = this
    self.$nextTick(function () {
      if (self.brandGroups.length > 0) self.selectedBrand = self.brandGroups[0].key
      if (self.currentModels.length > 0) self.selectModel(self.currentModels[0].id)
      self.updateToolScrollState()
      self.updateAppScrollState()
      var wrapper = self.$refs.toolsScrollWrapper
      if (wrapper) wrapper.addEventListener('scroll', function () { self.updateToolScrollState() })
      var appsWrapper = self.$refs.appsScrollWrapper
      if (appsWrapper) appsWrapper.addEventListener('scroll', function () { self.updateAppScrollState() })
      self.observePrimaryChatCard()
    })
    document.addEventListener('click', self.closePicker)
  },
  beforeDestroy: function () {
    document.removeEventListener('click', this.closePicker)
    if (this._primaryChatObserver) {
      this._primaryChatObserver.disconnect()
      this._primaryChatObserver = null
    }
    if (this._exploreChatObserver) {
      this._exploreChatObserver.disconnect()
      this._exploreChatObserver = null
    }
    if (this._primaryChatFallbackHandler) {
      window.removeEventListener('scroll', this._primaryChatFallbackHandler)
      window.removeEventListener('resize', this._primaryChatFallbackHandler)
      this._primaryChatFallbackHandler = null
    }
  },
  methods: {
    setExploreChatCardVisible: function (visible) {
      var shouldShow = !!visible
      var isNewlyShown = shouldShow && !this.showExploreChatCard
      if (isNewlyShown) {
        this.exploreChatAnimationKey += 1
        this.exploreChatAnimationActive = false
        this.exploreChatExpansionFinished = false
        this.exploreChatExpanded = false
      }
      this.showExploreChatCard = shouldShow
      if (isNewlyShown) {
        this.observeExploreChatCard()
      } else if (!shouldShow) {
        this.exploreChatAnimationActive = false
        this.exploreChatExpansionFinished = false
        this.exploreChatExpanded = false
        if (this._exploreChatObserver) {
          this._exploreChatObserver.disconnect()
          this._exploreChatObserver = null
        }
        this.showModelPicker = false
      }
    },
    observeExploreChatCard: function () {
      var self = this
      self.$nextTick(function () {
        var card = self.$refs.exploreChatCard
        if (!card) return
        if (self._exploreChatObserver) self._exploreChatObserver.disconnect()
        if (!('IntersectionObserver' in window)) {
          self.exploreChatAnimationActive = true
          return
        }
        self._exploreChatObserver = new IntersectionObserver(function (entries) {
          var entry = entries[0]
          if (!entry || !entry.isIntersecting) return
          self.exploreChatAnimationActive = true
          self._exploreChatObserver.disconnect()
          self._exploreChatObserver = null
        }, { threshold: 0.15 })
        self._exploreChatObserver.observe(card)
      })
    },
    expandExploreChatCard: function () {
      if (this.exploreChatExpanded) return
      this.exploreChatExpansionFinished = false
      this.exploreChatExpanded = true
      this.$nextTick(function () {
        var textarea = this.$refs.explorePromptTextarea
        if (!textarea || typeof textarea.focus !== 'function') return
        try {
          textarea.focus({ preventScroll: true })
        } catch (e) {
          textarea.focus()
        }
      })
    },
    onExploreChatSlotTransitionEnd: function (event) {
      if (event.target !== event.currentTarget || event.propertyName !== 'max-height') return
      if (this.exploreChatAnimationActive) this.exploreChatExpansionFinished = true
    },
    observePrimaryChatCard: function () {
      var card = this.$refs.primaryChatCard
      if (!card) return
      var self = this
      if ('IntersectionObserver' in window) {
        this._primaryChatObserver = new IntersectionObserver(function (entries) {
          var entry = entries[0]
          self.setExploreChatCardVisible(!!entry && !entry.isIntersecting && entry.boundingClientRect.bottom <= 0)
        }, { threshold: 0 })
        this._primaryChatObserver.observe(card)
        return
      }
      this._primaryChatFallbackHandler = function () {
        var rect = card.getBoundingClientRect()
        self.setExploreChatCardVisible(rect.bottom <= 0)
      }
      window.addEventListener('scroll', this._primaryChatFallbackHandler, { passive: true })
      window.addEventListener('resize', this._primaryChatFallbackHandler)
      this._primaryChatFallbackHandler()
    },
    onTabClick: function (tab) {
      this.activeTab = tab.key
    },
    selectModel: function (modelId) {
      this.selectedModelId = modelId
      this.showModelPicker = false
      this.modelSearch = ''
      var groups = this.brandGroups
      for (var i = 0; i < groups.length; i++) {
        var models = groups[i].models
        for (var j = 0; j < models.length; j++) {
          if (models[j].id === modelId) {
            this.selectedBrand = groups[i].key
            break
          }
        }
      }
      var defaults = getDefaultValues(modelId)
      this.paramValues = Object.assign({}, defaults)
    },
    formatParamPreview: function (param, value) {
      if (value === null || value === undefined || value === '') return ''
      /* 按钮组：优先用 optionLabels 转换（可能是翻译键，需要 $t） */
      if (param.type === 'button-group') {
        if (param.optionLabels && param.optionLabels[value]) return this.$t(param.optionLabels[value])
        return String(value)
      }
      /* 尺寸网格 */
      if (param.type === 'size-grid') {
        if (typeof value === 'object' && value.label) return value.label
        return String(value)
      }
      /* 分辨率芯片 */
      if (param.type === 'resolution-chips') return String(value)
      /* 卡片选择（optionLabels 可能是翻译键） */
      if (param.type === 'card-select') {
        if (param.optionLabels && param.optionLabels[value]) return this.$t(param.optionLabels[value])
        return String(value)
      }
      /* 滑块：带单位 */
      if (param.type === 'slider') {
        var unit = (param.props && param.props.unit) || 's'
        return value + unit
      }
      /* 开关：为 true 时显示简短标签 */
      if (param.type === 'switch') {
        if (!value) return ''
        var switchLabels = {
          generateAudio: 'Audio',
          promptEnhance: 'Enhanced',
          promptExtend: 'Extended',
          promptUpsampling: 'Upsampled',
          addWatermark: 'Watermark'
        }
        return switchLabels[param.key] || 'On'
      }
      return String(value)
    },
    onParamChange: function (key, value) {
      this.$set(this.paramValues, key, value)
    },
    onParamUpload: function (payload) {
      this.$set(this.uploadLoadingMap, payload.paramKey, true)
      var self = this
      var uploadFile = require('../api/upload').uploadFile
      uploadFile(payload.file).then(function (url) {
        self.$set(self.paramValues, payload.paramKey, url)
        self.$set(self.uploadFileNames, payload.paramKey, payload.file.name)
      }).catch(function () {}).then(function () {
        self.$set(self.uploadLoadingMap, payload.paramKey, false)
      })
    },
    onRemoveUpload: function (key) {
      this.$set(this.paramValues, key, null)
    },
    handleGenerate: function () {
      if (!this.prompt.trim()) return
      if (!this.isLoggedIn) {
        this.$store.dispatch('showLoginModal')
        return
      }
      var lang = this.$route.params.lang
      var pricingPath = lang ? '/' + lang + '/pricing' : '/pricing'
      if (this.insufficientCredits) {
        this.$router.push(pricingPath)
        return
      }
      /* 音乐标签：跳转到音乐页面 */
      if (this.activeTab === 'music') {
        var musicPath = lang ? '/' + lang + '/ai-music-generator' : '/ai-music-generator'
        var query = { songTitle: this.musicPreset, prompt: this.prompt.trim() }
        this.$router.push({ path: musicPath, query: query })
        return
      }
      if (!this.selectedModelId) return
      /* 查找模型的 routeId（版本号 URL 别名） */
      var routeId = this.selectedModelId
      var models = this.currentModels || []
      for (var mi = 0; mi < models.length; mi++) {
        if (models[mi].id === this.selectedModelId && models[mi].routeId) {
          routeId = models[mi].routeId
          break
        }
      }
      var prefix = '/models/'
      var path = prefix + routeId
      if (lang) path = '/' + lang + path
      /* 将 prompt 和参数通过 query 传递给目标页面 */
      var query = { prompt: this.prompt.trim() }
      var simpleParams = {}
      var keys = Object.keys(this.paramValues)
      for (var i = 0; i < keys.length; i++) {
        if (keys[i] === 'prompt') continue /* prompt 已单独传递 */
        var v = this.paramValues[keys[i]]
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
          simpleParams[keys[i]] = v
        }
      }
      if (Object.keys(simpleParams).length > 0) {
        query.params = JSON.stringify(simpleParams)
      }
      window.__autogenerate = true
      this.$router.push({ path: path, query: query })
    },
    closePicker: function (e) {
      if (!e.target.closest('.toolbar-model') && !e.target.closest('.model-dropdown')) {
        this.showModelPicker = false
      }
    },
    toolPath: function (toolId) {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + '/tools/' + toolId : '/tools/' + toolId
    },
    getQuickToolSeoKeyword: function (tool) {
      var fallback = this.$t('tools.' + tool.id + '.desc')
      return quickToolSeoKeywords.getQuickToolSeoKeyword(tool.id, this.$i18n.locale, fallback)
    },
    getToolPath: function (tool) {
      var lang = this.$route.params.lang
      var base = lang ? '/' + lang : ''
      if (tool.category === 'document') {
        return base + '/tools/doc-convert/' + tool.id
      }
      return base + '/tools/' + tool.id
    },
    scrollHorizontalPage: function (wrapper, dir, updateFn) {
      if (!wrapper) return
      updateFn()
      var max = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth)
      var current = wrapper.scrollLeft
      if (dir > 0 && current >= max - 1) return
      if (dir < 0 && current <= 1) return
      var next = Math.min(max, Math.max(0, current + dir * wrapper.clientWidth))
      if (Math.abs(next - current) < 1) return
      wrapper.scrollTo({ left: next, behavior: 'smooth' })
    },
    scrollTools: function (dir) {
      var self = this
      this.scrollHorizontalPage(this.$refs.toolsScrollWrapper, dir, function () {
        self.updateToolScrollState()
      })
    },
    updateToolScrollState: function () {
      var wrapper = this.$refs.toolsScrollWrapper
      if (!wrapper) return
      this.toolScrollPos = wrapper.scrollLeft
      this.toolMaxScroll = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth)
    },
    scrollApps: function (dir) {
      var self = this
      this.scrollHorizontalPage(this.$refs.appsScrollWrapper, dir, function () {
        self.updateAppScrollState()
      })
    },
    updateAppScrollState: function () {
      var wrapper = this.$refs.appsScrollWrapper
      if (!wrapper) return
      this.appScrollPos = wrapper.scrollLeft
      this.appMaxScroll = Math.max(0, wrapper.scrollWidth - wrapper.clientWidth)
    },
    onQuickAppEnter: function (e) {
      var video = e.currentTarget.querySelector('video')
      if (!video) return
      if (this._quickAppPlayingVideo && this._quickAppPlayingVideo !== video) {
        this._quickAppPlayingVideo.pause()
        this._quickAppPlayingVideo.currentTime = 0
      }
      if (!video.src) {
        var src = video.getAttribute('data-src')
        if (src) video.src = src
      }
      if (typeof video.play === 'function') {
        video.play().catch(function () {})
        this._quickAppPlayingVideo = video
      }
    },
    onQuickAppLeave: function (e) {
      var video = e.currentTarget.querySelector('video')
      if (!video) return
      video.pause()
      video.currentTime = 0
      if (this._quickAppPlayingVideo === video) {
        this._quickAppPlayingVideo = null
      }
    },
    appPath: function (appKey) {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + '/apps/' + appKey : '/apps/' + appKey
    },
    getDemoImages: function (toolId) {
      return getToolDemoImages(toolId)
    },
    isDocTool: function (toolId) {
      return isDocumentTool(toolId)
    },
    getToolText: function (id, field) {
      var tools = this.$t('tools')
      if (tools && typeof tools === 'object' && tools[id] && tools[id][field]) {
        return tools[id][field]
      }
      return this.$t('tools.' + id + '.' + field)
    },
    getExploreToolLabel: function (id) {
      var name = String(this.getToolText(id, 'name') || '')
      return name
        .replace(/^\s*免费\s*/, '')
        .replace(/^\s*Free\s+/i, '')
        .replace(/^\s*Gratis\s+/i, '')
        .replace(/^\s*Gratuit\s+/i, '')
        .replace(/^\s*Kostenlos(?:e[rsn])?\s+/i, '')
        .replace(/^\s*無料\s*/, '')
        .replace(/^\s*무료\s*/, '')
        .trim()
    }
  }
}
</script>

<style scoped>
.home-new {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 80px 24px 40px;
  background: #f7f7f8;
}

/* ===== 对话框卡片 ===== */
.home-chat-card {
  width: 100%;
  max-width: 860px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: visible;
}

/* ===== 标题 ===== */
.chat-hero-title {
  text-align: center;
  padding: 0 20px 36px;
  font-size: 32px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: -0.3px;
}

/* ===== 选项卡 ===== */
.chat-tabs {
  display: flex;
  gap: 2px;
  padding: 12px 20px 0;
}
.chat-tab {
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 500;
  color: #8b8fa3;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
}
.chat-tab:hover { color: #555; }
.chat-tab.active {
  color: #1a1a2e;
  font-weight: 600;
  border-bottom-color: #059669;
}

/* ===== 输入区 ===== */
.chat-body {
  padding: 16px 20px 8px;
}
.chat-textarea {
  width: 100%;
  min-height: 80px;
  padding: 0;
  border: none;
  font-size: 15px;
  font-family: inherit;
  color: #1a1a2e;
  background: transparent;
  resize: none;
  outline: none;
  line-height: 1.6;
}
.chat-textarea::placeholder { color: #b4b7c9; }
.chat-textarea-footer {
  display: flex;
  justify-content: space-between;
  padding: 4px 0 8px;
}
.chat-count { font-size: 11px; color: #c5c8d8; }

/* ===== 底部工具栏（参考 Pollo 布局） ===== */
.chat-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid #f0f1f3;
  background: #fafafa;
  border-radius: 0 0 20px 20px;
}

/* 模型选择 */
.toolbar-model {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #1a1a2e;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}
.toolbar-model:hover { background: #eee; }
.toolbar-model-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toolbar-divider {
  width: 1px;
  height: 20px;
  background: #e0e1e5;
  flex-shrink: 0;
}

/* 参数标签区（Pollo AI 风格 pill 标签） */
.toolbar-params {
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 8px;
  transition: background 0.15s;
}
.param-chip {
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: #e8e8ed;
  color: #444;
  white-space: nowrap;
  line-height: 1.6;
  flex-shrink: 0;
  transition: background 0.15s;
}
.toolbar-params:hover .param-chip { background: #dddde2; }
.param-chip-more {
  background: #ddd;
  color: #888;
  font-size: 11px;
  padding: 3px 7px;
}
.param-chevron {
  color: #aaa;
  flex-shrink: 0;
  transition: color 0.15s;
}
.toolbar-params:hover .param-chevron { color: #666; }

/* 生成按钮 */
.toolbar-generate {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: #059669;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}
.toolbar-generate:hover:not(:disabled) { background: #047857; }
.toolbar-generate-host {
  display: inline-flex;
  flex-shrink: 0;
  vertical-align: middle;
}
.toolbar-generate-host--disabled {
  cursor: default;
  position: relative;
}
.toolbar-generate-host--disabled:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  background: rgba(17, 24, 39, 0.94);
  color: #fff;
  font-size: 12px;
  line-height: 1.3;
  padding: 6px 10px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 20;
}
.toolbar-generate:disabled { opacity: 0.35; cursor: inherit; }
.toolbar-credits {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 10px;
  padding: 0;
  flex-shrink: 0;
}
.toolbar-credits-label {
  font-size: 11px;
  color: #8b8fa3;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.toolbar-credits-value {
  font-size: 14px;
  font-weight: 700;
  color: #059669;
}

/* ===== 模型下拉（两列联动） ===== */
.model-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 16px;
  right: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.1);
  overflow: hidden;
  z-index: 100;
}
.model-dropdown-inner {
  display: flex;
  min-height: 280px;
}
.brand-column {
  width: 140px;
  border-right: 1px solid #f0f1f3;
  background: #fafafa;
  padding: 8px;
  overflow-y: auto;
  flex-shrink: 0;
}
.brand-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  border: none;
  background: none;
  text-align: left;
  transition: background 0.15s;
}
.brand-item:hover { background: #f0f0f2; }
.brand-item.active {
  background: rgba(5, 150, 105, 0.08);
  color: #059669;
  font-weight: 600;
}
.brand-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.brand-count {
  font-size: 11px;
  color: #aaa;
  background: #f0f0f2;
  padding: 1px 6px;
  border-radius: 4px;
  flex-shrink: 0;
  margin-left: 4px;
}
.brand-item.active .brand-count {
  background: rgba(5, 150, 105, 0.12);
  color: #059669;
}
.models-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.models-search {
  padding: 8px;
  border-bottom: 1px solid #f0f1f3;
}
.models-search input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #333;
  outline: none;
  font-family: inherit;
}
.models-search input:focus { border-color: #059669; }
.models-list {
  max-height: 240px;
  overflow-y: auto;
  padding: 6px;
}
.model-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  border: none;
  background: none;
  text-align: left;
  transition: background 0.15s;
}
.model-item:hover { background: #f5f5f7; }
.model-item.active { background: rgba(5, 150, 105, 0.08); color: #059669; }
.model-item-left { display: flex; align-items: center; gap: 6px; }
.model-item-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.model-item-badge {
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.5;
  background: rgba(5, 150, 105, 0.1);
  color: #059669;
  flex-shrink: 0;
}
.model-item-badge.badge-new {
  background: #f4c430;
  color: #fff;
}
.model-item-badge.badge-hot {
  background: #ef4444;
  color: #fff;
}
.model-item-credits { font-size: 11px; color: #aaa; flex-shrink: 0; margin-left: 8px; }
.model-dropdown-empty {
  padding: 20px;
  text-align: center;
  font-size: 13px;
  color: #aaa;
}

/* ===== 参数弹窗 ===== */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.param-modal {
  background: #fff;
  border-radius: 18px;
  padding: 28px;
  width: 480px;
  max-width: 90vw;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.param-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  background: #fff;
  padding: 0 0 12px;
  border-bottom: 1px solid #f0f1f3;
  margin-bottom: 0;
}
.param-modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-top: 20px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.param-modal-body::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}
.param-modal-header h3 { font-size: 17px; font-weight: 700; color: #1a1a2e; }
.pm-close {
  font-size: 22px; color: #aaa;
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 8px; cursor: pointer; border: none; background: none;
}
.pm-close:hover { background: #f0f0f0; color: #555; }

/* ===== 参数弹窗内白色主题覆盖 ===== */
.param-modal {
  --bg-primary: #fff;
  --bg-card: #f7f7f8;
  --bg-card-border: #e5e7eb;
  --text-primary: #1a1a2e;
  --text-secondary: #555;
  --text-muted: #888;
  --accent: #059669;
  --accent-hover: #047857;
  --accent-light: rgba(5, 150, 105, 0.08);
  --accent-border: rgba(5, 150, 105, 0.25);
  --border-color: #e0e1e5;
  --border-light: #f0f1f3;
  color: #333;
}
.param-modal >>> .option-group label { color: #333; }
/* 弹窗内不用等分四列网格：窄格会把长文案挤出按钮；改为自动宽度 + 换行排布 */
.param-modal >>> .btn-group {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 8px;
  margin-top: 8px;
}
.param-modal >>> .param-item { margin-bottom: 14px; background: none !important; border: none !important; box-shadow: none !important; border-radius: 0 !important; }
.param-modal >>> .param-item:last-child { margin-bottom: 0; }
.param-modal >>> .switch-title { color: #333; font-size: 13px; font-weight: 600; }
.param-modal >>> .switch-row { color: #555; }
.param-modal >>> .form-group label { color: #333; }
.param-modal >>> .optional-label { color: #999; font-size: 12px; font-weight: 400; margin-left: 8px; }
/* 弹窗内所有“选项按钮”统一视觉规格 */
.param-modal >>> .btn-toggle,
.param-modal >>> .resolution-chip,
.param-modal >>> .size-btn,
.param-modal >>> .gen-type-card {
  background: #f7f7f8;
  border: 1px solid #e0e1e5;
  border-radius: 8px;
  font-size: 13px;
  box-sizing: border-box;
  transition: all 0.2s;
}
.param-modal >>> .btn-toggle {
  color: #555;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: auto;
  max-width: 100%;
  min-width: 75px;
  min-height: 38px;
  padding: 8px 12px;
  font-size: 13px;
  white-space: nowrap;
  flex-shrink: 0;
  text-align: center;
  box-sizing: border-box;
}
.param-modal >>> .btn-toggle:hover { border-color: #059669; }
.param-modal >>> .btn-toggle.active {
  background: #059669; border-color: #059669; color: #fff;
}
.param-modal >>> .prompt-input {
  background: #f7f7f8; border-color: #e0e1e5; color: #333;
}
.param-modal >>> .prompt-input:focus { border-color: #059669; }
.param-modal >>> .seed-input {
  background: #f7f7f8; border: 1px solid #e0e1e5; color: #333; border-radius: 8px; padding: 10px 14px; outline: none;
}
.param-modal >>> .seed-input:focus { border-color: #059669; }
.param-modal >>> .upload-zone {
  background: #f7f7f8; border-color: #ddd; color: #666;
  border-width: 1.5px; padding: 20px; border-radius: 12px; min-height: 120px;
}
.param-modal >>> .upload-zone:hover { border-color: #059669; background: rgba(5, 150, 105, 0.04); }
.param-modal >>> .upload-zone .upload-hint { color: #999; }
.param-modal >>> .upload-zone .upload-preview { max-height: 160px; border-radius: 8px; }
.param-modal >>> .upload-preview-sm { max-width: 140px; max-height: 100px; border-radius: 8px; }
.param-modal >>> .upload-zone .upload-preview-label { color: #555; }
.param-modal >>> .remove-image-btn { background: rgba(0,0,0,0.5); width: 22px; height: 22px; font-size: 12px; }
.param-modal >>> .remove-image-btn:hover { background: rgba(255,0,0,0.6); }
.param-modal >>> .switch-slider { background: #d1d5db; }
.param-modal >>> input:checked + .switch-slider { background: #059669; }
.param-modal >>> .size-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}
.param-modal >>> .size-btn {
  min-height: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  overflow: hidden;
  box-sizing: border-box;
}
.param-modal >>> .size-btn:hover { border-color: #059669; }
.param-modal >>> .size-btn.active { border-color: #059669; background: #059669; }
.param-modal >>> .size-label {
  color: #666;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}
.param-modal >>> .size-btn.active .size-label { color: #fff; }
.param-modal >>> .gen-type-cards {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.param-modal >>> .gen-type-card {
  min-height: 40px;
  padding: 8px 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.param-modal >>> .gen-type-card:hover { border-color: #059669; }
.param-modal >>> .gen-type-card.active { border-color: #059669; background: #059669; }
.param-modal >>> .gen-type-card-name {
  color: #333;
  text-align: center;
  width: 100%;
  line-height: 1.3;
  word-break: break-word;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}
.param-modal >>> .gen-type-card.active .gen-type-card-name { color: #fff; }
.param-modal >>> .resolution-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.param-modal >>> .resolution-chip {
  color: #555;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 8px 14px;
  white-space: nowrap;
  flex: 0 0 auto;
}
.param-modal >>> .resolution-chip:hover { border-color: #059669; }
.param-modal >>> .resolution-chip.active { border-color: #059669; background: #059669; color: #fff; }
.param-modal >>> .loading-spinner { border-color: #e0e1e5; border-top-color: #059669; }

/* ===== 音乐风格下拉 ===== */
.toolbar-music-style {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}
.music-style-select-wrap {
  position: relative;
  flex: 0 1 280px;
  width: 280px;
  max-width: 100%;
}
.music-style-select {
  width: 100%;
  padding: 8px 36px 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  font-size: 13px;
  color: #333;
  outline: none;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  transition: border-color 0.2s;
}
.music-style-select::-ms-expand {
  display: none;
}
.music-style-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  pointer-events: none;
}
.music-style-select:focus {
  border-color: #059669;
}

/* ===== 快捷工具区 ===== */
.quick-tools-section {
  width: 100%;
  max-width: 1400px;
  margin: 52px auto 0;
  padding: 0 48px;
  box-sizing: border-box;
}
.quick-tools-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.quick-tools-title {
  font-size: 14px;
  font-weight: 600;
  color: #8b8fa3;
  padding-left: 4px;
}
.quick-tools-nav {
  display: flex;
  gap: 6px;
}
.quick-tools-arrow {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
}
.quick-tools-arrow:hover:not(:disabled) {
  border-color: #059669;
  color: #059669;
}
.quick-tools-arrow:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.quick-tools-scroll-wrapper {
  padding: 2px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
}
.quick-tools-scroll-wrapper::-webkit-scrollbar { display: none; }
.quick-tools-track {
  display: flex;
  gap: 12px;
  padding-bottom: 4px;
}
.quick-tools-section:not(.quick-apps-section) .quick-tools-track {
  display: grid;
  grid-template-rows: repeat(2, auto);
  grid-auto-flow: column;
  grid-auto-columns: calc((100% - 48px) / 5);
  gap: 12px;
}
.quick-tools-section:not(.quick-apps-section) .quick-tool-card {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.quick-tool-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  padding: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  width: calc(20% - 10px);
  min-width: 120px;
  overflow: hidden;
}
.quick-tool-card:hover {
  border-color: #059669;
  box-shadow: 0 4px 16px rgba(5, 150, 105, 0.1);
  transform: translateY(-2px);
}
.quick-tool-cover {
  display: flex;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #f3f4f6;
  overflow: hidden;
}
.quick-tool-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
}
.quick-tool-fallback-icon {
  font-size: 24px;
  line-height: 1;
}
.quick-tool-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  padding: 10px 8px 12px;
  min-height: 72px;
  box-sizing: border-box;
}
.quick-tool-name {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: #1a1a2e;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  padding: 0;
}
.quick-tool-desc {
  font-size: 11px;
  color: #8b8fa3;
  text-align: center;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding: 0;
  width: 100%;
}

.quick-apps-section {
  margin-top: 44px;
}
.quick-apps-section .quick-tool-card {
  aspect-ratio: 1 / 2;
}
.quick-apps-section .quick-tool-cover {
  flex: 1 1 auto;
  min-height: 0;
  aspect-ratio: auto;
}
.quick-apps-section .quick-tool-body {
  flex: 0 0 72px;
  min-height: 72px;
}
.quick-apps-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 12px;
  margin-left: 2px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  white-space: nowrap;
}
.quick-apps-more:hover {
  border-color: #059669;
  color: #059669;
}
.quick-app-cover-img,
.quick-app-cover-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.quick-app-cover-video {
  background: #f3f4f6;
}
.quick-apps-section .quick-tool-desc {
  -webkit-line-clamp: 1;
}

/* ===== 探索更多 AI 功能 ===== */
.explore-more-section {
  width: 100%;
  max-width: 1400px;
  margin: 52px auto 0;
  padding: 0 48px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.explore-more-header {
  order: 1;
  margin-bottom: 18px;
}
.explore-chat-card-slot {
  order: 3;
  width: 100%;
  max-height: 1px;
  overflow: hidden;
  transition: max-height 1.25s cubic-bezier(0.65, 0, 0.35, 1);
}
.explore-chat-card-slot--expanded {
  max-height: 160px;
}
.explore-chat-card-slot--full {
  max-height: 430px;
}
.explore-chat-card-slot--open {
  overflow: visible;
}
.explore-chat-compact {
  width: 100%;
  max-width: 860px;
  height: 132px;
  margin: 28px auto 0;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid rgba(5, 150, 105, 0.24);
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(5, 150, 105, 0.13), 0 2px 8px rgba(0, 0, 0, 0.04);
  opacity: 0;
  transform: translateY(-12px) scale(0.985);
  transition: opacity 0.65s ease 0.18s, transform 0.85s cubic-bezier(0.22, 1, 0.36, 1) 0.12s;
}
.explore-chat-card-slot--expanded .explore-chat-compact {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.explore-chat-compact-main {
  min-height: 0;
  display: flex;
  flex: 1;
  align-items: center;
}
.explore-chat-compact-input {
  width: 100%;
  height: 100%;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: linear-gradient(135deg, #f8fffc 0%, #f7f7f8 100%);
  transition: border-color 0.2s, background 0.2s;
}
.explore-chat-compact-input:hover {
  border-color: rgba(5, 150, 105, 0.35);
  background: #f4fbf8;
}
.explore-chat-compact-prompt {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 6px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #9ca3af;
  font: inherit;
  text-align: left;
  cursor: text;
  overflow: hidden;
}
.explore-chat-compact-prompt:hover {
  background: transparent;
}
.explore-chat-compact-icon {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: #fff;
  background: linear-gradient(135deg, #10b981, #047857);
  box-shadow: 0 5px 14px rgba(5, 150, 105, 0.22);
}
.explore-chat-compact-content {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
}
.explore-chat-compact-text {
  display: block;
  overflow: hidden;
  color: #9ca3af;
  font-size: 15px;
  line-height: 1.4;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.explore-chat-compact-text.has-value {
  color: #374151;
}
.explore-chat-compact .toolbar-generate-host,
.explore-chat-compact .toolbar-generate {
  height: 48px;
}
.home-chat-card--explore {
  max-width: 860px;
  margin: 28px auto 0;
  transform-origin: center top;
  opacity: 0;
  transform: translateY(-14px) scale(0.99);
}
.explore-chat-card-slot--full .home-chat-card--explore {
  opacity: 1;
  transform: translateY(0) scale(1);
  animation: explore-chat-full-in 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes explore-chat-full-in {
  from {
    opacity: 0;
    transform: translateY(-14px) scale(0.99);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
@media (prefers-reduced-motion: reduce) {
  .explore-chat-card-slot {
    transition: none;
  }
  .explore-chat-card-slot--expanded {
    overflow: visible;
  }
  .home-chat-card--explore {
    transition: none;
  }
  .explore-chat-card-slot--full .home-chat-card--explore {
    opacity: 1;
    transform: none;
    animation: none;
  }
}
.explore-more-grid {
  order: 2;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 12px;
}
.explore-more-item {
  position: relative;
  flex: 0 1 max-content;
  min-width: 150px;
  max-width: 420px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: border-color 0.2s, box-shadow 0.2s;
}
.explore-more-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.explore-more-cover {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.explore-more-cover-video {
  background: #e5e7eb;
  pointer-events: none;
}
.explore-more-doc {
  position: absolute;
  inset: 0;
  display: flex;
  background: #f3f4f6;
}
.explore-more-doc-side {
  flex: 1;
  min-width: 0;
}
.explore-more-item--doc .explore-more-link::before {
  background: rgba(255, 255, 255, 0.62);
}
.explore-more-fallback {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  font-size: 16px;
}
.explore-more-link {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0 14px;
  text-decoration: none;
  box-sizing: border-box;
}
.explore-more-link::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.82);
  transition: background 0.3s;
}
.explore-more-item:hover .explore-more-link::before {
  background: rgba(0, 0, 0, 0.42);
}
.explore-more-label {
  position: relative;
  z-index: 1;
  display: block;
  width: auto;
  max-width: 100%;
  text-align: center;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.3s;
}
.explore-more-item:hover .explore-more-label {
  color: #fff;
}

/* ===== 响应式 ===== */
@media (max-width: 1024px) {
  .quick-tools-section { margin-top: 44px; padding: 0 24px; }
  .quick-apps-section { margin-top: 38px; }
  .explore-more-section { margin-top: 44px; padding: 0 24px; }
}
@media (max-width: 768px) {
  .home-new { padding: 20px 12px; align-items: flex-start; padding-top: 60px; }
  .home-chat-card { border-radius: 16px; }
  .chat-tabs { padding: 10px 12px 0; }
  .chat-tab { padding: 6px 14px; font-size: 12px; }
  .chat-body { padding: 12px 14px 6px; }
  .chat-textarea { min-height: 70px; font-size: 14px; }
  .chat-toolbar { padding: 8px 10px; gap: 6px; flex-wrap: wrap; }
  .toolbar-generate { padding: 7px 14px; font-size: 13px; }
  .model-dropdown { left: 8px; right: 8px; }
  .model-dropdown-inner { flex-direction: column; min-height: auto; }
  .brand-column { width: 100%; border-right: none; border-bottom: 1px solid #f0f1f3; display: flex; flex-wrap: wrap; gap: 4px; padding: 6px; }
  .brand-item { padding: 6px 10px; font-size: 12px; width: auto; }
  .models-list { max-height: 180px; }
  .quick-tools-section { margin-top: 36px; padding: 0 12px; }
  .quick-apps-section { margin-top: 32px; }
  .quick-tools-track { gap: 8px; align-items: stretch; }
  .quick-tools-section:not(.quick-apps-section) .quick-tools-track {
    grid-auto-columns: calc((100% - 16px) / 3);
    gap: 8px;
  }
  .quick-tool-card { width: calc(33.33% - 6px); min-width: 90px; }
  .quick-tool-body { min-height: 68px; padding: 8px 6px 10px; }
  .quick-apps-section .quick-tool-body { flex-basis: 68px; min-height: 68px; }
  .quick-tool-fallback-icon { font-size: 20px; }
  .quick-tool-name { font-size: 13px; }
  .quick-tool-desc { font-size: 10px; }
  .quick-apps-more { padding: 0 10px; font-size: 11px; }
  .explore-more-section { margin-top: 40px; padding: 0 12px; }
  .explore-more-grid { gap: 10px; }
  .explore-more-item { flex-basis: max-content; min-width: 140px; max-width: 100%; height: 44px; }
  .explore-more-label { font-size: 12px; }
}
</style>
